import { FC, ReactNode, useCallback, useState } from 'react';
import stylex from '@stylexjs/stylex';
import { PlusIcon } from '@shared/assets/icons';
import { CustomDropdown } from '@shared/ui/dropdown';
import { AddGuestButton } from '../AddGuestButton';
import { ExtraGuestRow } from '../MainGuestTab/ExtraGuestRow';
import { styles } from './styles';
import type { MainGuestDropdownOption } from '@widgets/segments-tab';

interface ExtraGuestItem {
  id: number;
  lastName: string;
  firstName: string;
  patronymic: string;
}

export const AdditionalGuestTab: FC<{
  extraGuestsSection?: ReactNode;
  mainGuestOptions?: MainGuestDropdownOption[];
  value?: MainGuestDropdownOption | null;
  onMainGuestChange?: (option: MainGuestDropdownOption | null) => void;
}> = ({ extraGuestsSection, mainGuestOptions = [], value: controlledValue, onMainGuestChange }) => {
  const [internalValue, setInternalValue] = useState<MainGuestDropdownOption | null>(null);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;
  const changeFunc = useCallback(
    (option: MainGuestDropdownOption | null) => {
      if (isControlled) {
        onMainGuestChange?.(option);
        return;
      }
      setInternalValue(option);
    },
    [isControlled, onMainGuestChange]
  );
  const [extraAdults, setExtraAdults] = useState<ExtraGuestItem[]>([]);
  const [extraChildren, setExtraChildren] = useState<ExtraGuestItem[]>([]);

  const addAdult = () =>
    setExtraAdults((prev) => [...prev, { id: Date.now(), lastName: '', firstName: '', patronymic: '' }]);
  const addChild = () =>
    setExtraChildren((prev) => [...prev, { id: Date.now(), lastName: '', firstName: '', patronymic: '' }]);
  const removeAdult = (id: number) => setExtraAdults((prev) => prev.filter((item) => item.id !== id));
  const removeChild = (id: number) => setExtraChildren((prev) => prev.filter((item) => item.id !== id));
  const updateAdult = (id: number, field: keyof Omit<ExtraGuestItem, 'id'>, val: string) => {
    setExtraAdults((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: val } : item)));
  };
  const updateChild = (id: number, field: keyof Omit<ExtraGuestItem, 'id'>, val: string) => {
    setExtraChildren((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: val } : item)));
  };

  const hasAddedGuests = extraAdults.length > 0 || extraChildren.length > 0;

  return (
    <div {...stylex.props(styles.sections)}>
      <section {...stylex.props(styles.section)}>
        <h3 {...stylex.props(styles.mainGuestSectionTitle)}>Основной гость</h3>
        <div {...stylex.props(styles.mainGuestBlock)}>
          <CustomDropdown<MainGuestDropdownOption>
            placeholder="Выберите основного гостя"
            options={mainGuestOptions}
            itemText="label"
            itemValue="id"
            value={value}
            changeFunc={changeFunc}
            isRadio={false}
            fullWidth
            searchable
            searchPlaceholder="Поиск гостей"
          />
        </div>
      </section>
      {extraGuestsSection ?? (
        <section {...stylex.props(styles.section, styles.sectionExtraGuests)}>
          <h3 {...stylex.props(styles.sectionTitle)}>Дополнительные гости</h3>
          <div {...stylex.props(styles.extraGuestsContent, !hasAddedGuests && styles.extraGuestsContentNoGap)}>
            <div {...stylex.props(styles.adultContainer)}>
              {extraAdults.map((item, index) => (
                <ExtraGuestRow
                  key={item.id}
                  item={item}
                  title={`Взрослый гость ${index + 1}`}
                  onUpdate={updateAdult}
                  onRemove={removeAdult}
                />
              ))}
              {extraChildren.map((item, index) => (
                <ExtraGuestRow
                  key={item.id}
                  item={item}
                  title={`Маленький гость ${index + 1}`}
                  onUpdate={updateChild}
                  onRemove={removeChild}
                />
              ))}
            </div>
            <div {...stylex.props(styles.addGuestButtons)}>
              <AddGuestButton icon={<PlusIcon height="20" width="20" />} onClick={addAdult}>
                Взрослый
              </AddGuestButton>
              <AddGuestButton icon={<PlusIcon height="20" width="20" />} onClick={addChild}>
                Маленький
              </AddGuestButton>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
